import { sendAnalytics } from '@playcastdotio/Analytics';
import type {
  PlaycastAnalyticsRewardRedeemed,
  PlaycastAnalyticsPointsEarned,
  PlaycastAnalyticsRewardsCatalogueViewed,
  PlaycastAnalyticsRedeemAttemptFailed,
  PlaycastAnalyticsRewardsHubOpened,
  PlaycastAnalyticsOnboardingWelcomeViewed,
  PlaycastAnalyticsOnboardingWelcomeCompleted,
  PlaycastAnalyticsOnboardingAddPcViewed,
  PlaycastAnalyticsOnboardingAddPcAttempted,
  PlaycastAnalyticsRestartPromptShown,
  PlaycastAnalyticsRestartNowClicked,
  PlaycastAnalyticsRestartLaterClicked,
  PlaycastAnalyticsRestartScheduleCreated,
  PlaycastAnalyticsRestartScheduleCancelled,
  PlaycastAnalyticsRestartScheduleExpired,
  PlaycastAnalyticsPostRebootRewardFired,
  PlaycastAnalyticsHostModeActivationStarted,
  PlaycastAnalyticsHostModeActivationSucceeded,
  PlaycastAnalyticsHostModeActivationBlocked,
  PlaycastAnalyticsHostModeActivationFailed,
  PlaycastAnalyticsHostModeModalDismissed,
  PlaycastAnalyticsRewardsHubVariantShown,
} from '@playcastdotio/Analytics';

export const rewardsFlowMethods = {

  // ── Rewards ──

  sendRewardRedeemedEvent(params: { skuId: string; skuName: string; pointCost: number; monetaryValue?: number }): void {
    sendAnalytics({ name: 'Reward Redeemed', role: 'host', ...params } as PlaycastAnalyticsRewardRedeemed);
  },

  sendPointsEarnedEvent(params: { ruleName: string; ruleId: string; pointsEarned: number }): void {
    sendAnalytics({ name: 'Points Earned', role: 'host', ...params } as PlaycastAnalyticsPointsEarned);
  },

  sendCatalogueViewedEvent(): void {
    sendAnalytics({ name: 'Rewards Catalogue Viewed', role: 'host' } as PlaycastAnalyticsRewardsCatalogueViewed);
  },

  sendRedeemAttemptFailedEvent(params: { skuId: string; reason: string }): void {
    sendAnalytics({ name: 'Redeem Attempt Failed', role: 'host', ...params } as PlaycastAnalyticsRedeemAttemptFailed);
  },

  sendRewardsHubOpenedEvent(): void {
    sendAnalytics({ name: 'Rewards Hub Opened', role: 'host' } as PlaycastAnalyticsRewardsHubOpened);
  },

  // ── Onboarding ──

  sendOnboardingWelcomeViewedEvent(params: { tier: string; machineId: string }): void {
    sendAnalytics({ name: 'Onboarding Welcome Viewed', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsOnboardingWelcomeViewed);
  },

  sendOnboardingWelcomeCompletedEvent(params: { tier: string; viewDurationMs: number }): void {
    sendAnalytics({ name: 'Onboarding Welcome Completed', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsOnboardingWelcomeCompleted);
  },

  sendOnboardingAddPcViewedEvent(params: { attestationStatus: string }): void {
    sendAnalytics({ name: 'Onboarding Add PC Viewed', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsOnboardingAddPcViewed);
  },

  sendOnboardingAddPcAttemptedEvent(params: { attemptNumber: number; success: boolean; errorReason?: string }): void {
    sendAnalytics({ name: 'Onboarding Add PC Attempted', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsOnboardingAddPcAttempted);
  },

  // ── Restart / Reboot ──

  sendRestartPromptShownEvent(params: { trigger: 'auto' | 'manual'; hasIncentive: boolean; incentiveAmount?: number }): void {
    sendAnalytics({ name: 'Restart Prompt Shown', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsRestartPromptShown);
  },

  sendRestartNowClickedEvent(params: { source: 'prompt' | 'confirm' | 'schedule' }): void {
    sendAnalytics({ name: 'Restart Now Clicked', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsRestartNowClicked);
  },

  sendRestartLaterClickedEvent(): void {
    sendAnalytics({ name: 'Restart Later Clicked', role: 'host', tier2Filters: ['machine'] } as PlaycastAnalyticsRestartLaterClicked);
  },

  sendRestartScheduleCreatedEvent(params: { scheduledTime: string; delayMinutes: number }): void {
    sendAnalytics({ name: 'Restart Schedule Created', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsRestartScheduleCreated);
  },

  sendRestartScheduleCancelledEvent(): void {
    sendAnalytics({ name: 'Restart Schedule Cancelled', role: 'host', tier2Filters: ['machine'] } as PlaycastAnalyticsRestartScheduleCancelled);
  },

  sendRestartScheduleExpiredEvent(): void {
    sendAnalytics({ name: 'Restart Schedule Expired', role: 'host', tier2Filters: ['machine'] } as PlaycastAnalyticsRestartScheduleExpired);
  },

  sendPostRebootRewardFiredEvent(params: { version: string; machineId: string }): void {
    sendAnalytics({ name: 'Post Reboot Reward Fired', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsPostRebootRewardFired);
  },

  // ── Host Mode Activation ──

  sendHostModeActivationStartedEvent(): void {
    sendAnalytics({ name: 'Host Mode Activation Started', role: 'host', tier2Filters: ['machine'] } as PlaycastAnalyticsHostModeActivationStarted);
  },

  sendHostModeActivationSucceededEvent(params: { totalDurationMs: number }): void {
    sendAnalytics({ name: 'Host Mode Activation Succeeded', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsHostModeActivationSucceeded);
  },

  sendHostModeActivationBlockedEvent(params: { phase: string; failingGates: string[] }): void {
    sendAnalytics({ name: 'Host Mode Activation Blocked', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsHostModeActivationBlocked);
  },

  sendHostModeActivationFailedEvent(params: { error: string; phase: string; errorCode?: string }): void {
    sendAnalytics({ name: 'Host Mode Activation Failed', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsHostModeActivationFailed);
  },

  sendHostModeModalDismissedEvent(params: { phase: string; action: string }): void {
    sendAnalytics({ name: 'Host Mode Modal Dismissed', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsHostModeModalDismissed);
  },

  // ── Hub ──

  sendRewardsHubVariantShownEvent(params: { variant: 'pending-restart' | 'normal'; hasScheduledReboot: boolean }): void {
    sendAnalytics({ name: 'Rewards Hub Variant Shown', role: 'host', tier2Filters: ['machine'], ...params } as PlaycastAnalyticsRewardsHubVariantShown);
  },
};

export type RewardsFlowMethods = typeof rewardsFlowMethods;
